import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, User, ArrowRight, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { useBlog } from '../hooks/useData';
import LoadingSpinner from './ui/LoadingSpinner';
import Carousel from './ui/Carousel';
import { Link } from 'react-router-dom';

// Componente para o card do artigo
const BlogCard: React.FC<{ post: any }> = ({ post }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const toHref = post.slug && String(post.slug).trim().length > 0 ? `/blog/${post.slug}` : '/blog';
  return (
    <Link to={toHref} className="block h-full no-underline">
      <motion.article
        className="card card-pad group hover:shadow-lg transition-shadow duration-300 h-full"
      >
      {/* Imagem do artigo */}
      <div className="aspect-video bg-gradient-to-br from-brand-accent to-brand-slate rounded-lg mb-4 flex items-center justify-center">
        <BookOpen className="w-12 h-12 text-brand-sand" />
      </div>

      {/* Categoria */}
      <div className="mb-3">
        <span className="badge text-xs">
          {post.category}
        </span>
      </div>

      {/* Título */}
      <h3 className="text-xl font-heading text-brand-slate mb-3 group-hover:text-brand-accent transition-colors">
        {post.title}
      </h3>

      {/* Excerpt */}
      <p className="text-brand-ink/80 text-sm leading-relaxed mb-4">
        {post.excerpt}
      </p>

      {/* Meta informações */}
      <div className="flex items-center gap-4 text-xs text-brand-ink/60 mb-4">
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(post.date)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{post.read_time}</span>
        </div>
        <div className="flex items-center gap-1">
          <User className="w-3 h-3" />
          <span>{post.author}</span>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-4">
        {(Array.isArray(post.tags) ? post.tags : []).slice(0, 3).map((tag: string, tagIndex: number) => (
          <span
            key={tagIndex}
            className="px-2 py-1 bg-brand-slate/10 text-brand-slate text-xs rounded-full"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Link para ler mais */}
      <div className="flex items-center gap-2 text-sm text-brand-slate group-hover:text-brand-accent transition-colors">
        Ler artigo completo
        <ArrowRight className="w-4 h-4" />
      </div>
      </motion.article>
    </Link>
  );
};

const BlogSection: React.FC = () => {
  const { t } = useTranslation();
  const { blogData, loading, error } = useBlog();

  const fallbackBlog = [
    {
      id: 1,
      title: "Como proteger sua empresa de passivos trabalhistas",
      slug: "como-proteger-sua-empresa-de-passivos-trabalhistas",
      excerpt: "Descubra as principais estratégias preventivas para evitar processos trabalhistas e garantir a segurança jurídica do seu negócio.",
      content: "Conteúdo completo não disponível offline.",
      author: "Luara Rezende",
      category: "Gestão & Empresas",
      tags: ["Preventivo", "Empresas", "RH"],
      read_time: "5 min",
      featured: true,
      image: "",
      date: new Date().toISOString()
    },
    {
      id: 2,
      title: "Guia prático para combater a inadimplência em condomínios",
      slug: "guia-pratico-inadimplencia-condominios",
      excerpt: "Passo a passo sobre as medidas extrajudiciais e judiciais mais eficazes para recuperar o fluxo de caixa do seu condomínio.",
      content: "Conteúdo completo não disponível offline.",
      author: "Luara Rezende",
      category: "Vida em Condomínio",
      tags: ["Condomínio", "Inadimplência", "Síndico"],
      read_time: "4 min",
      featured: true,
      image: "",
      date: new Date(Date.now() - 86400000).toISOString()
    }
  ];

  const dataToUse = error || (!loading && blogData.length === 0) ? fallbackBlog : blogData;

  // Pegar os artigos mais recentes para o carrossel
  const recentPosts = dataToUse
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 6); // Mais artigos para o carrossel


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="blog" className="section bg-brand-sand">
      <div className="wrapper">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-brand-accent" />
              <h2 className="text-4xl md:text-5xl font-heading text-brand-slate">
                {t('content.title')}
              </h2>
            </div>
            <p className="text-lg text-brand-ink/90 max-w-3xl mx-auto">
              {t('content.subtitle')}
            </p>
          </motion.div>

          {loading ? (
            <LoadingSpinner message="Carregando artigos..." />
          ) : recentPosts.length > 0 ? (
            <>
              {/* Carrossel de artigos */}
              <motion.div variants={itemVariants} className="mb-12">
                <Carousel
                  itemsPerView={2}
                  autoPlay={true}
                  autoPlayInterval={6000}
                  showControls={true}
                  showDots={true}
                  className="max-w-7xl mx-auto"
                >
                  {recentPosts.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </Carousel>
              </motion.div>

              {/* Botão para ver todos os artigos */}
              <motion.div variants={itemVariants} className="text-center">
                <Link to="/blog" className="btn-primary">
                  <BookOpen className="w-5 h-5" />
                  Ver todos os artigos
                </Link>
              </motion.div>
            </>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-16 h-16 text-brand-ink/30 mx-auto mb-4" />
              <h3 className="text-xl font-heading text-brand-slate mb-2">
                Nenhum artigo encontrado
              </h3>
              <p className="text-brand-ink/70">
                Não há artigos disponíveis no momento.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;
